// « Animations réduites » (RGAA 13.8 / WCAG 2.2.2), en un seul endroit.
//
// Le réglage était lu à deux endroits qui n'ont pas le même besoin, et un troisième
// arrivait avec le retour en haut de page (components/LienAccueil.tsx) :
//
//   · le logo le lit EN CONTINU — sa vague est du CSS qui doit s'arrêter dès que le
//     réglage change, sans recharger la page : c'est `useAnimationsReduites()` ;
//   · un gestionnaire de clic le lit UNE FOIS, au moment du geste, pour choisir entre un
//     défilement doux et un saut : c'est `animationsReduites()`. Un abonnement n'y
//     apporterait rien qu'un rendu de plus.
//
// Les deux partagent la même chaîne de requête média — c'est tout l'intérêt de ce fichier :
// une faute de frappe dans l'une des copies serait silencieuse (une requête média inconnue
// répond simplement « non »), et le mouvement continuerait chez qui a demandé qu'il cesse.
import { useSyncExternalStore } from 'react'

export const MQ_ANIMATIONS_REDUITES = '(prefers-reduced-motion: reduce)'

/**
 * Lecture ponctuelle du réglage — pour un gestionnaire d'événement.
 *
 * `window.matchMedia?.()` et non `window.matchMedia()` : l'API manque dans certains
 * environnements de rendu (même précaution que components/AnchorLink.tsx). Absente, on
 * répond `true`, donc « pas d'animation » : ne pas bouger est toujours le défaut sûr.
 */
export function animationsReduites(): boolean {
  return window.matchMedia?.(MQ_ANIMATIONS_REDUITES).matches ?? true
}

/** Le défilement qui découle du réglage : un saut, ou une glissade. */
export function comportementDefilement(): ScrollBehavior {
  return animationsReduites() ? 'auto' : 'smooth'
}

/**
 * Vrai si le système demande des animations réduites (RGAA 13.8 / WCAG 2.2.2).
 *
 * Pourquoi un abonnement et pas une simple lecture : le réglage peut changer PENDANT la
 * session (macOS et Windows l'exposent dans un panneau qu'on ouvre sans recharger).
 * Une lecture au montage laisserait la marque bouger chez qui vient de demander qu'elle
 * s'arrête — pour une animation d'agrément, c'est exactement le cas à traiter.
 *
 * Ce n'est PAS un minuteur : `matchMedia` ne réveille rien tant que le réglage ne change
 * pas, et l'animation elle-même est du CSS pur, que le navigateur suspend tout seul quand
 * l'onglet passe en arrière-plan. Aucun `setInterval` ne tourne derrière ce logo.
 *
 * Instantané « serveur » = `true`, donc STATIQUE : c'est celui que reçoit le prérendu, et
 * un logo figé qui se met à respirer est moins fautif que l'inverse.
 */
export function useAnimationsReduites(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia?.(MQ_ANIMATIONS_REDUITES)
      if (!mq) return () => {}
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    },
    animationsReduites,
    () => true,
  )
}
